import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, JourneyShoppingPathRow } from "@/lib/api";
import {
  JOURNEY_WINDOW_OPTIONS,
  JourneyWindowId,
  journeyWindowParams,
} from "@/lib/journeyWindow";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InfoTip, LabelWithTip } from "@/components/ui/tooltip";
import StatsExplainer from "@/components/StatsExplainer";
import JourneyEntryGroupsSplit from "@/components/JourneyEntryGroupsSplit";
import { cn } from "@/lib/utils";
import type { MetricTipId } from "@/lib/metricsGlossary";

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "-";
  return `${Math.round(v * 1000) / 10}%`;
}

export default function JourneyShoppingPaths() {
  const [period, setPeriod] = useState<JourneyWindowId>("7");

  const query = useQuery({
    queryKey: ["site-analytics-journey-shopping-paths", period],
    queryFn: () => api.siteAnalyticsJourney(journeyWindowParams(period)),
    retry: false,
  });

  const paths = query.data?.shopping_paths;

  return (
    <div className="grid gap-5">
      <StatsExplainer variant="sciezki" />

      <div className="flex flex-wrap items-center gap-2">
        {JOURNEY_WINDOW_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={opt.id === period ? "default" : "outline"}
            onClick={() => setPeriod(opt.id)}
          >
            {opt.label}
          </Button>
        ))}
        <span className="ml-2 text-xs text-muted-foreground">
          {query.data?.period?.label_pl ?? "czas PL"}
          {" · "}
          Tylko wizyty, które otworzyły koszyk. Sumy, bez pojedynczych osób.
        </span>
      </div>

      {query.data?.ok && (
        <JourneyEntryGroupsSplit channels={query.data.acquisition_channels} />
      )}

      {query.isLoading && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Wczytywanie ścieżek…
          </CardContent>
        </Card>
      )}
      {query.isError && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Nie udało się wczytać danych.
          </CardContent>
        </Card>
      )}

      {paths && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat tipId="cart_sessions_ours" value={String(paths.sessions)} />
            <Stat
              tipId="purchases_on_path"
              value={String(paths.purchased)}
              hint={pct(paths.purchase_rate)}
            />
            <Stat tipId="abandoned_carts" value={String(paths.abandoned)} />
            <Stat tipId="cart_to_purchase" value={pct(paths.purchase_rate)} hint={paths.note} />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <MixCard title="Strona wejścia" rows={paths.entry_mix ?? []} />
            <MixCard title="Skąd przyszli" rows={paths.traffic_mix ?? []} />
            <MixCard title="Koszyk / produkt" rows={paths.product_mix ?? []} />
          </div>

          <PathTable
            title="Najczęstsze ścieżki stron"
            tipId="path_order"
            subtitle="Kolejność stron w jednej wizycie: wejście → strony → koszyk → wynik"
            rows={paths.top_page_paths ?? []}
            showPurchaseRate
          />
          <PathTable
            title="Najczęstsze ścieżki sekcji"
            subtitle="Wejście → sekcje (skrócone) → koszyk → wynik"
            rows={paths.top_section_paths ?? []}
          />

          <div className="grid gap-4 lg:grid-cols-2">
            <PathTable
              title="Ścieżki zakończone zakupem"
              rows={paths.purchased_page_paths ?? []}
              empty="Brak zakupów w okresie."
            />
            <PathTable
              title="Ścieżki z porzuceniem koszyka"
              rows={paths.abandoned_page_paths ?? []}
              empty="Brak porzuceń w okresie."
            />
          </div>
        </>
      )}
    </div>
  );
}

function Stat({
  tipId,
  value,
  hint,
}: {
  tipId: MetricTipId;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="text-xs text-muted-foreground">
          <LabelWithTip tipId={tipId} />
        </div>
        <div className="text-xl font-semibold tabular-nums">{value}</div>
        {hint && <div className="mt-1 text-[11px] leading-snug text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function MixCard({
  title,
  rows,
}: {
  title: string;
  rows: { key: string; sessions: number; share: number }[];
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.length === 0 && (
          <p className="text-sm text-muted-foreground">Brak danych.</p>
        )}
        {rows.map((r) => (
          <div key={r.key} className="flex items-center justify-between gap-2 text-sm">
            <span className="truncate">{r.key}</span>
            <span className="shrink-0 font-mono tabular-nums text-muted-foreground">
              {r.sessions} · {pct(r.share)}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function PathTable({
  title,
  tipId,
  subtitle,
  rows,
  showPurchaseRate,
  empty = "Brak ścieżek w tym okresie.",
}: {
  title: string;
  tipId?: MetricTipId;
  subtitle?: string;
  rows: JourneyShoppingPathRow[];
  showPurchaseRate?: boolean;
  empty?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-1 text-base">
          {title}
          {tipId && <InfoTip tipId={tipId} />}
        </CardTitle>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">{empty}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead>
                <tr className="border-b border-border/60 text-xs text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">#</th>
                  <th className="py-2 pr-3 font-medium">Ścieżka</th>
                  <th className="py-2 pr-3 font-medium">
                    <span className="inline-flex items-center gap-1">
                      Wizyty
                      <InfoTip tipId="visits" />
                    </span>
                  </th>
                  <th className="py-2 pr-3 font-medium">Udział</th>
                  {showPurchaseRate && (
                    <th className="py-2 font-medium">
                      <span className="inline-flex items-center gap-1">
                        Kupili (%)
                        <InfoTip tipId="cvr_to_purchase" />
                      </span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={`${r.path}-${i}`}
                    className={cn("border-b border-border/40", i % 2 === 1 && "bg-muted/20")}
                  >
                    <td className="py-2.5 pr-3 font-mono tabular-nums text-muted-foreground">
                      {i + 1}
                    </td>
                    <td className="py-2.5 pr-3 leading-snug">{r.path}</td>
                    <td className="py-2.5 pr-3 font-mono tabular-nums">{r.sessions}</td>
                    <td className="py-2.5 pr-3 font-mono tabular-nums">{pct(r.share)}</td>
                    {showPurchaseRate && (
                      <td className="py-2.5 font-mono tabular-nums">
                        {pct(r.purchase_rate)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
